import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Building2, Wrench, Bell, Wallet, Sparkles, Lock } from "lucide-react";

const Card = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`rounded-2xl border border-navy/10 bg-white p-5 shadow-lg shadow-navy/[0.06] ${className}`}>{children}</div>
);

const Label = ({ icon: Icon, children, tone = "text-slate2" }) => (
  <p className={`flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.18em] ${tone}`}>
    {Icon && <Icon className="h-3 w-3" />} {children}
  </p>
);

export const PortfolioHealthCard = ({ className = "" }) => (
  <Card testId="glimpse-portfolio-health" className={className}>
    <div className="flex items-center justify-between">
      <Label icon={Building2}>Portfolio Health</Label>
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3">
      {[["8", "Properties"], ["94%", "Occupancy"], ["3", "Need attention"]].map(([v, k], i) => (
        <div key={k} className="min-w-0">
          <p className={`font-display text-2xl font-extrabold tracking-tight ${i === 2 ? "text-gold" : "text-navy-ink"}`}>{v}</p>
          <p className="truncate text-[11px] text-slate2">{k}</p>
        </div>
      ))}
    </div>
    <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-mist">
      <div className="h-full w-[82%] rounded-full bg-navy" />
    </div>
    <p className="mt-2 text-[11px] text-slate2">Overall health: strong</p>
  </Card>
);

export const MaintenanceCard = ({ className = "" }) => (
  <Card testId="glimpse-maintenance" className={className}>
    <Label icon={Wrench}>Preventive Maintenance</Label>
    <p className="mt-3 font-display text-base font-bold text-navy-ink">Roof Inspection</p>
    <p className="mt-0.5 text-sm text-slate2">Due in 12 days</p>
    <span className="mt-4 inline-flex items-center gap-1 font-display text-xs font-bold text-navy">Schedule <ArrowRight className="h-3.5 w-3.5" /></span>
  </Card>
);

export const PortfolioAlertCard = ({ className = "" }) => (
  <Card testId="glimpse-portfolio-alert" className={`!bg-navy !border-navy text-white ${className}`}>
    <Label icon={Bell} tone="text-gold-soft">Portfolio Alert</Label>
    <p className="mt-3 font-display text-base font-bold leading-snug">3 properties require attention</p>
    <span className="mt-4 inline-flex items-center gap-1 font-display text-xs font-bold text-gold-soft">View Insights <ArrowRight className="h-3.5 w-3.5" /></span>
  </Card>
);

export const FinancialCard = ({ className = "" }) => (
  <Card testId="glimpse-financial" className={className}>
    <Label icon={Wallet}>Financial Performance</Label>
    <p className="mt-3 text-sm text-slate2">Operating costs</p>
    <p className="mt-0.5 flex items-center gap-1.5 font-display text-2xl font-extrabold tracking-tight text-emerald-600"><ArrowDownRight className="h-5 w-5" /> 7.4%</p>
    <p className="mt-1 text-[11px] text-slate2">vs previous quarter</p>
  </Card>
);

export const OccupancyCard = ({ className = "" }) => (
  <Card testId="glimpse-occupancy" className={className}>
    <Label>Occupancy</Label>
    <p className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy-ink">94%</p>
    <div className="mt-3 flex gap-1">
      {Array.from({ length: 12 }).map((_, i) => <span key={i} className={`h-6 flex-1 rounded-sm ${i < 11 ? "bg-navy" : "bg-mist"}`} />)}
    </div>
  </Card>
);

export const InsightTeaserCard = ({ className = "" }) => (
  <Card testId="glimpse-insight" className={`!border-gold/30 ${className}`}>
    <Label icon={Sparkles} tone="text-gold">ANFORD Insight</Label>
    <p className="mt-3 flex items-center gap-2 font-display text-lg font-extrabold tracking-tight text-navy-ink">Maintenance Costs <span className="inline-flex items-center text-red-500"><ArrowUpRight className="h-5 w-5" />18%</span></p>
    <p className="mt-2 text-sm leading-relaxed text-slate2">Maintenance expenditure at East Legon Office has increased over the last quarter.</p>
    <div className="mt-4 rounded-xl border-l-2 border-gold bg-mist/70 p-3.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gold">Recommended action</p>
      <p className="mt-1.5 select-none text-sm font-medium text-navy-ink blur-[5px]" aria-hidden="true">Review the vendor contract and schedule an inspection of the affected systems before the next quarter.</p>
      <Link to="/demo" data-testid="glimpse-insight-unlock" className="mt-2 inline-flex min-h-[40px] items-center gap-1.5 font-display text-xs font-bold text-navy hover:text-gold">
        <Lock className="h-3.5 w-3.5" /> See the recommendation in a demo <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  </Card>
);

export const AbstractDashboard = () => (
  <div aria-hidden="true" className="pointer-events-none select-none rounded-2xl border border-navy/10 bg-white p-4 opacity-70 [mask-image:linear-gradient(to_bottom,black_20%,transparent_95%)]">
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full bg-navy/15" /><span className="h-2.5 w-2.5 rounded-full bg-navy/15" /><span className="h-2.5 w-2.5 rounded-full bg-navy/15" />
      <span className="ml-3 h-2 w-1/3 rounded bg-mist" />
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="min-w-0 rounded-xl border border-navy/10 p-3"><span className="block h-2 w-1/2 rounded bg-mist" /><span className="mt-2 block h-4 w-3/4 rounded bg-navy/10" /></div>
      ))}
    </div>
    <div className="mt-3 grid grid-cols-3 gap-3">
      <div className="col-span-2 min-w-0 rounded-xl border border-navy/10 p-3">
        <span className="block h-2 w-1/2 rounded bg-mist" />
        <div className="mt-3 flex h-24 items-end gap-1.5">
          {[40, 55, 48, 62, 58, 70, 66, 74, 71, 80, 78, 86].map((h, i) => <span key={i} className="flex-1 rounded-t bg-navy/15" style={{ height: `${h}%` }} />)}
        </div>
      </div>
      <div className="min-w-0 space-y-2.5 rounded-xl border border-navy/10 p-3">
        {Array.from({ length: 5 }).map((_, i) => <span key={i} className="block h-2 rounded bg-mist" style={{ width: `${90 - i * 12}%` }} />)}
      </div>
    </div>
  </div>
);
